// (8) Scaffold level content: theory note, worked-examples carousel, practice
// questions (immediate feedback), explore-more side-branch, and the next-level
// / re-attempt-original CTAs.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiPost } from '@/lib/api';
import type { Question, ScaffoldAttemptResult, ScaffoldLevelDetail } from '@/types';
import { MathText } from '@/components/exam/MathText';
import { MCQInput } from '@/components/exam/MCQInput';

interface Props {
  subtopicId: string;
  level: number;
  onAdvance: () => void;
  onSidebranch: () => void;
  onReattemptOriginal: () => void;
}

export function LevelContent({ subtopicId, level, onAdvance, onSidebranch, onReattemptOriginal }: Props) {
  const [exampleIdx, setExampleIdx] = useState(0);
  const [showSidebranch, setShowSidebranch] = useState(false);
  const [passed, setPassed] = useState(false);
  const [reattempt, setReattempt] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['scaffold-level', subtopicId, level],
    queryFn: () => apiGet<ScaffoldLevelDetail>(`/scaffold/${subtopicId}/levels/${level}`),
  });

  async function submit(question: Question, selectedIdx: number) {
    const res = await apiPost<ScaffoldAttemptResult>(`/scaffold/${subtopicId}/attempt`, {
      questionId: question.id,
      answer: String(selectedIdx),
      answerMethod: 'OPTION',
      level,
    });
    if (res.isCorrect) {
      setPassed(true);
      setFeedback(
        res.nextAction === 'reattempt_original'
          ? "Correct! You're ready for the original question."
          : 'Correct! Ready for the next level.',
      );
      if (res.nextAction === 'reattempt_original') setReattempt(true);
    } else {
      setFeedback('Not quite — try a side-branch question to build it up.');
    }
    return res;
  }

  if (isLoading) return <div className="h-64 animate-pulse rounded-2xl bg-paper-sunk" />;
  if (!data) return <p className="p-6 text-center" style={{ color: 'var(--ink-3)' }}>Couldn't load this level.</p>;

  const examples = data.workedExamples ?? [];
  const ex = examples[exampleIdx];
  const questions = showSidebranch ? data.sidebranchQuestions : data.practiceQuestions;
  const cardStyle = { background: 'var(--surface-card)', border: '1px solid var(--border-default)' };

  return (
    <div className="space-y-4">
      {/* Theory note (simple markdown-ish rendering: paragraphs by blank line) */}
      <section className="rounded-2xl p-4" style={cardStyle}>
        <h3 className="mb-2 text-sm font-bold" style={{ color: 'var(--text-body)' }}>Theory note</h3>
        <div className="space-y-2 text-sm" style={{ color: 'var(--text-body)' }}>
          {data.theoryNote.split(/\n{2,}/).map((para, i) => (
            <p key={i}>
              <MathText>{para}</MathText>
            </p>
          ))}
        </div>
      </section>

      {/* Worked examples carousel */}
      {examples.length > 0 && (
        <section className="rounded-2xl p-4" style={cardStyle}>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-bold" style={{ color: 'var(--text-body)' }}>
              Worked example {exampleIdx + 1} / {examples.length}
            </h3>
            <div className="flex gap-1">
              <button
                onClick={() => setExampleIdx((i) => Math.max(0, i - 1))}
                disabled={exampleIdx === 0}
                className="rounded-full px-2 py-1 text-xs font-bold disabled:opacity-40"
                style={{ background: 'var(--paper-sunk)', color: 'var(--ink-2)' }}
              >
                ‹
              </button>
              <button
                onClick={() => setExampleIdx((i) => Math.min(examples.length - 1, i + 1))}
                disabled={exampleIdx === examples.length - 1}
                className="rounded-full px-2 py-1 text-xs font-bold disabled:opacity-40"
                style={{ background: 'var(--paper-sunk)', color: 'var(--ink-2)' }}
              >
                ›
              </button>
            </div>
          </div>
          <div className="rounded-lg p-3 text-sm" style={{ background: 'var(--surface-sunk)', color: 'var(--text-body)' }}>
            {ex?.step && <p className="font-semibold">{ex.step}</p>}
            {ex?.explanation && <p>{ex.explanation}</p>}
            {ex && !ex.step && !ex.explanation && <pre className="whitespace-pre-wrap text-xs">{JSON.stringify(ex)}</pre>}
          </div>
        </section>
      )}

      {/* Practice questions */}
      <section className="rounded-2xl p-4" style={cardStyle}>
        <h3 className="mb-2 text-sm font-bold" style={{ color: 'var(--text-body)' }}>
          {showSidebranch ? 'Side-branch practice' : 'Practice'}
        </h3>
        {questions.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>No practice questions at this level.</p>
        ) : (
          <div className="space-y-4">
            {questions.map((q) => (
              <div key={q.id}>
                <div className="mb-2" style={{ color: 'var(--text-strong)' }}>
                  <MathText>{q.text || 'Question'}</MathText>
                </div>
                <MCQInput options={q.options ?? []} onSubmit={(idx) => void submit(q, idx)} />
              </div>
            ))}
          </div>
        )}
        {feedback && <p className="mt-3 text-sm font-semibold" style={{ color: 'var(--text-body)' }}>{feedback}</p>}

        <button
          onClick={() => { setShowSidebranch(true); onSidebranch(); }}
          className="mt-3 text-sm font-medium"
          style={{ color: 'var(--ink-2)' }}
        >
          Explore more examples →
        </button>
      </section>

      {/* CTAs */}
      {reattempt ? (
        <button
          onClick={onReattemptOriginal}
          className="w-full rounded-2xl px-4 py-3 font-bold text-white transition"
          style={{ background: 'var(--green-border)' }}
        >
          Re-attempt the original question!
        </button>
      ) : (
        <button
          onClick={onAdvance}
          disabled={!passed}
          className="w-full rounded-2xl px-4 py-3 font-bold text-white transition disabled:opacity-40"
          style={{ background: 'var(--action-bg)' }}
        >
          I'm ready — next level
        </button>
      )}
    </div>
  );
}
